import * as React from 'react';
import { FC, useEffect } from 'react';
import { Chip } from '@mui/material';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { ClientEntity } from '../../store/entities/state';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { ClientOrder } from '../../models';
import { getClientOrdersAsync } from '../../ajax/requests';
import { formatEuros } from '../../shared/string';
import AppButton from '../../components/AppButton';
import Typography from '../../components/Typography';
import { ApplicationState, AppThunkDispatch } from '../../store';
import { entityCreating, entityEditing, loadEntities } from '../../store/entities/actions';
import { ClientOrderEntity } from '../../store/entities/state';
import { Guid } from '../../shared/utils';

export interface ClientOrdersTableProps {
    isOpen?: boolean;
    client: ClientEntity;
}

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-M);
`;

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacer-S);
    flex-wrap: wrap;
`;

const Title = styled.div`
    font-size: 1rem;
    font-weight: 700;
    color: var(--text-body);
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XXS);
`;

const OrderCard = styled.button.attrs(() => ({}))<{ $unpaid?: boolean }>`
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--spacer-M);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-left: var(--border-thick) solid ${p => p.$unpaid ? 'var(--danger-color)' : 'var(--appBlue)'};
    border-radius: 14px;
    background: linear-gradient(180deg, white 0%, var(--bg-light) 100%);
    padding: var(--spacer-S) var(--spacer-M);
    text-align: left;
    cursor: pointer;
    appearance: none;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    box-shadow: 0 1px 0 rgba(15, 23, 42, 0.03);

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
        border-color: ${p => p.$unpaid ? 'var(--danger-color)' : 'var(--appBlue)'};
    }
`;

const UnpaidBadge = styled(Chip)`
    && {
        height: 24px;
        border-radius: 999px;
        font-weight: 700;
        background: rgba(234, 140, 26, 0.12);
        color: #b45f06;
        border: 1px solid rgba(234, 140, 26, 0.24);

        .MuiChip-label {
            padding-left: var(--spacer-XS);
            padding-right: var(--spacer-XS);
            font-size: 0.74rem;
        }
    }
`;

const OrderMain = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XXS);
`;

const OrderDate = styled.div`
    font-weight: 700;
    color: var(--text-body);
`;

const OrderMeta = styled.div`
    font-size: 0.84rem;
    color: var(--text-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const OrderMetaItalic = styled(OrderMeta)`
    font-style: italic;
`;

const OrderPrice = styled.div`
    font-weight: 700;
    color: var(--text-body);
    white-space: nowrap;
    align-self: center;
`;

const EmptyState = styled.div`
    border: var(--border-thin) dashed var(--appLigthGrey);
    border-radius: 14px;
    background: var(--bg-light);
    padding: var(--spacer-M);
    color: var(--text-muted);
    font-style: italic;
`;

const ClientOrdersTable: FC<ClientOrdersTableProps> = ({ isOpen = true, client }) => {
    const t = useTranslations();
    const dispatch = useDispatch<AppThunkDispatch>();

    const loading = useSelector((state: ApplicationState) => state.entities.orders?.isLoading ?? false);
    const orderItems = useSelector((state: ApplicationState) => state.entities.orders?.items ?? {});
    const orderIds = useSelector((state: ApplicationState) => state.entities.orders?.searchResult ?? []);
    const orders = orderIds.map(id => orderItems[id]).filter(Boolean) as ClientOrderEntity[];

    useEffect(() => {
        if (!isOpen) return;
        dispatch(loadEntities('orders', () => getClientOrdersAsync(client.id, 50, 0) as Promise<ClientOrderEntity[]>, 'NewResult'));
    }, [client.id, isOpen, dispatch]);

    const sortedOrders = [...orders].sort(
        (a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime()
    );

    const handleAddOrder = () => {
        dispatch(entityCreating('orders', {
            id: Guid.newGuid(),
            clientId: client.id,
            clientName: client.name,
            creationDate: new Date().toISOString(),
            createdBy: '',
            items: [],
            payments: [],
            missingToPay: 0,
        } as ClientOrderEntity));
    };

    const handleEditOrder = (order: ClientOrder) => {
        dispatch(entityEditing('orders', order.id));
    };

    return (
        <Section>
            <Toolbar>
                <Title>{t(TK.orders)}</Title>
                <AppButton id={`client-${client.id}-add-order-button`} onClick={handleAddOrder}>
                    {t(TK.addOrder)}
                </AppButton>
            </Toolbar>

            {loading
                ? <Typography><i>{t(TK.loading)}…</i></Typography>
                : !sortedOrders.length
                    ? <EmptyState>{t(TK.noOrderAddedYet)}</EmptyState>
                    : <List>
                        {sortedOrders.map(order => {
                            const itemsSummary = order.items.map(i => `${i.name} ×${i.quantity}`).join(', ');
                            const total = order.items.reduce((s, i) => s + i.value * i.quantity * (1 + i.vat), 0);
                            const unpaid = (order.missingToPay ?? total) > 0.009;
                            return (
                                <OrderCard
                                    key={order.id}
                                    type="button"
                                    id={`client-${client.id}-order-${order.id}-button`}
                                    $unpaid={unpaid}
                                    onClick={() => handleEditOrder(order)}
                                >
                                    <OrderMain>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)' }}>
                                            <OrderDate>{order.creationDate.slice(0, 10)}</OrderDate>
                                            {unpaid && (
                                                <UnpaidBadge size="small" label={t(TK.missingPayment)} />
                                            )}
                                        </div>
                                        <OrderMeta>{itemsSummary}</OrderMeta>
                                        {order.notes && <OrderMetaItalic>{order.notes}</OrderMetaItalic>}
                                    </OrderMain>
                                    <OrderPrice>{formatEuros(total)}</OrderPrice>
                                </OrderCard>
                            );
                        })}
                    </List>
            }
        </Section>
    );
};

export default ClientOrdersTable;
